import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Tracks how many leads still need a first look from the receptionist.
 * When the count *increases* between polls, we play a soft chime via the
 * Web Audio API so office staff hear a new lead land anywhere in the app.
 *
 * A single module-level polling loop owns the state + the chime side-effect
 * — mounting the hook in multiple components (Layout + LeadsPage) doesn't
 * cause duplicate audio pings. Each hook instance is just a subscriber.
 */
const listeners = new Set();
let sharedCount = 0;
let baselineEstablished = false;
let pollTimer = null;
let inflight = false;
let refCount = 0;
let activeToken = null;

function broadcast(next) {
  sharedCount = next;
  listeners.forEach((cb) => cb(next));
}

/** Soft two-note "ping" via Web Audio — no file needed, low volume. */
function playChime() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return;
    const ctx = new Ctx();
    const now = ctx.currentTime;
    const notes = [
      { f: 880, t: 0.00, d: 0.18 },     // A5
      { f: 1318.5, t: 0.14, d: 0.22 },  // E6
    ];
    notes.forEach(({ f, t, d }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = f;
      gain.gain.setValueAtTime(0.0001, now + t);
      gain.gain.exponentialRampToValueAtTime(0.15, now + t + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + t + d);
      osc.connect(gain).connect(ctx.destination);
      osc.start(now + t);
      osc.stop(now + t + d + 0.02);
    });
    setTimeout(() => ctx.close?.(), 600);
  } catch (_) { /* audio not allowed yet — ignore */ }
}

async function fetchCountOnce() {
  if (inflight) return;
  inflight = true;
  try {
    const { data } = await api.get("/leads/unviewed-count");
    const next = Number(data?.count || 0);
    if (baselineEstablished && next > sharedCount) {
      playChime();
    }
    baselineEstablished = true;
    broadcast(next);
  } catch (_) {
    /* keep previous count on transient error */
  } finally {
    inflight = false;
  }
}

function startPolling() {
  if (pollTimer) return;
  fetchCountOnce();
  pollTimer = setInterval(fetchCountOnce, 30_000);
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  baselineEstablished = false;
  sharedCount = 0;
  activeToken = null;
}

export function useUnviewedLeads() {
  const { user } = useAuth();
  const [count, setCount] = useState(sharedCount);

  useEffect(() => {
    const eligible = user && (user.role === "admin" || user.role === "office");
    if (!eligible) {
      // If we were polling before and the user changed to a non-eligible one,
      // shut it down cleanly.
      if (activeToken) stopPolling();
      return;
    }
    const token = user.id;
    // User changed (e.g. logout → login as different account): reset state.
    if (activeToken && activeToken !== token) {
      stopPolling();
    }
    activeToken = token;

    const cb = (n) => setCount(n);
    listeners.add(cb);
    refCount++;
    startPolling();

    return () => {
      listeners.delete(cb);
      refCount = Math.max(0, refCount - 1);
      if (refCount === 0) stopPolling();
    };
  }, [user]);

  return { count, refresh: fetchCountOnce };
}
